// This is the React component for Section 6 ("OBJECTIVES")
// It uses a single, clean "waterfall" reveal for all screen sizes.
// The CSS file handles the layout changes (2-column vs. stacked).

import React, { useLayoutEffect, useRef } from "react";
import "./Section6.css"; // We will create this file next
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Import the image for the desktop column
import ladyRunningImg from "../../../assets/images/LadyRunning4.png";

gsap.registerPlugin(ScrollTrigger);

// --- Component Text ---
// An array of objects for the 7 objectives.
const objectives = [
  {
    title: "PILOT",
    text: "Build a one-year pilot program with up to 3 community groups.",
  },
  {
    title: "DEVELOPMENT PHASE:",
    text: "Develop an approach to running using activity cards that outline the building blocks of each phase.",
  },
  {
    title: "YOUTH LEADERSHIP",
    text: "Integrate 1-2 youth into the development of the program along with older teens to serve as role models and training support",
  },
  {
    title: "MENTORSHIP:",
    text: "Integrate “guides” to oversee the program from within each of the community groups.",
  },
  {
    title: "DURATION",
    text: "1-2 sessions per week and to be held over 4-6 weeks.",
  },
  {
    title: "IMPLEMENTATION",
    text: "2-3 programs through the year – measure impact and remain flexible.",
  },
  {
    title: "GOAL",
    text: "Use the Raven 50 Youth Relay as a participation goal over one year.",
  },
];

// --- React Component ---
function Section6() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null); // Ref for the main "OBJECTIVES" title
  const itemRefs = useRef([]); // Ref for the 7 objective <li> elements

  useLayoutEffect(() => {
    // Add all 7 list items to the itemRefs array
    itemRefs.current = itemRefs.current.slice(0, objectives.length);

    const ctx = gsap.context(() => {
      // --- "Nice Slow Waterfall" Animation (All Sizes) ---
      // We animate the title and all items in one sequence.
      gsap.fromTo(
        [titleRef.current, ...itemRefs.current], // Animate title AND items
        { opacity: 0, y: 50 }, // From: hidden and 50px down
        {
          opacity: 1,
          y: 0, // To: visible and in place
          duration: 1, // This controls the speed of *each* item
          ease: "power2.out",
          // This creates the "slow waterfall"
          stagger: 0.2, // 0.2s delay between each item
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 75%", // Start when 75% from top
            toggleActions: "play none none none",
            once: true, // Only run once
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // --- JSX (HTML) ---
  return (
    // This section has the mobile background image and overlay
    // applied via Section6.css
    <section className="section-6" ref={sectionRef}>
      {/* This container holds the 2 desktop columns */}
      <div className="section-6-container">
        
        {/* COLUMN 1: TEXT CONTENT */}
        <div className="section-6-text-column">
          <h2 className="section-6-title" ref={titleRef}>
            OBJECTIVES
          </h2>

          <ul className="section-6-list">
            {objectives.map((item, index) => (
              <li
                className="section-6-objective"
                key={index}
                // We add each <li> to our itemRefs array
                ref={(el) => (itemRefs.current[index] = el)}
              >
                <h3 className="section-6-objective-title">{item.title}</h3>
                <p className="section-6-objective-text">{item.text}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* COLUMN 2: DESKTOP IMAGE */}
        {/* This column is hidden on mobile by CSS */}
        <div className="section-6-image-column">
          <img
            src={ladyRunningImg}
            alt="Silhouette of a person running"
          />
        </div>
      </div>
    </section>
  );
}

export default Section6;